import {
  FileCodeIcon,
  FileIcon,
  ImageIcon,
  LinkIcon,
  TableIcon,
  VideoIcon,
} from "lucide-react";

import { FileChooser } from "simplydo/react/components/Choosers/FileChooser/FileChooser";
import { IFrameChooser } from "simplydo/react/components/Choosers/IFrameChooser";
import { ImageChooser } from "simplydo/react/components/Choosers/ImageChooser";
import { LinkChooser } from "simplydo/react/components/Choosers/LinkChooser";
import { VideoChooser } from "simplydo/react/components/Choosers/VideoChooser";
import type { EditorMenuItem } from "simplydo/react/richtext/BasicRichText";
import { useEditorContext } from "simplydo/react/richtext/editor/EditorContext";
import {
  extractHTMLTag,
  iframeRegex,
} from "simplydo/react/richtext/editor/extensions/iframe";

import type { ChooserState, SetChooser } from "../menuTypes";

export const getFullMenu = (
  chooser: ChooserState,
  setChooser: SetChooser,
  forType: string | undefined,
  forId: string | undefined,
): Array<EditorMenuItem> => [
  {
    name: "link",
    icon: LinkIcon,
    onClick: ({ commands, isActive, selectedText }) => {
      if (isActive) {
        commands.removeLink?.();
      } else {
        setChooser("link", !chooser.link?.open, selectedText);
      }
    },
    MenuItem: ({ RenderItem, itemProps }) => {
      const { commands } = useEditorContext();
      return (
        <LinkChooser
          trigger={
            <div>
              <RenderItem {...itemProps} />
            </div>
          }
          isOpen={!!chooser.link?.open}
          shouldFix={false}
          selectedText={chooser.link?.text ?? ""}
          onClose={() => setChooser("link", false)}
          onComplete={(text, url) => {
            commands.createCustomLink?.({ text, href: url });
            setChooser("link", false);
          }}
        />
      );
    },
  },
  {
    name: "image",
    icon: ImageIcon,
    onClick: ({ selectedText }) => {
      setChooser("image", !chooser.image?.open, selectedText);
    },
    MenuItem: ({ RenderItem, itemProps }) => {
      const { commands } = useEditorContext();
      return (
        <ImageChooser
          trigger={
            <div>
              <RenderItem {...itemProps} />
            </div>
          }
          forType={forType ?? ""}
          forId={forId ?? ""}
          isOpen={!!chooser.image?.open}
          onClose={() => setChooser("image", false)}
          onComplete={(_image, _name, imageSrc) => {
            commands.insertImage?.({ src: imageSrc });
            setChooser("image", false);
          }}
        />
      );
    },
  },
  {
    name: "video",
    icon: VideoIcon,
    onClick: ({ selectedText }) => {
      setChooser("video", !chooser.video?.open, selectedText);
    },
    MenuItem: ({ RenderItem, itemProps }) => {
      const { commands } = useEditorContext();
      return (
        <VideoChooser
          trigger={
            <div>
              <RenderItem {...itemProps} />
            </div>
          }
          forType={forType ?? ""}
          forId={forId ?? ""}
          isOpen={!!chooser.video?.open}
          onClose={() => setChooser("video", false)}
          onComplete={(video, _name, videoSrc) => {
            const isEmbed = video.startsWith("embed-");
            const src = isEmbed ? video.replace("embed-", "") : videoSrc;
            if (isEmbed) {
              commands.addIframe?.({ src });
            } else {
              commands.insertVideo?.({ src });
            }
            setChooser("video", false);
          }}
        />
      );
    },
  },
  {
    name: "file",
    icon: FileIcon,
    onClick: ({ isActive, selectedText }) => {
      if (isActive) return;
      setChooser("file", !chooser.file?.open, selectedText);
    },
    MenuItem: ({ RenderItem, itemProps }) => {
      const { commands } = useEditorContext();
      return (
        <FileChooser
          trigger={
            <div>
              <RenderItem {...itemProps} />
            </div>
          }
          forType={forType ?? ""}
          forId={forId ?? ""}
          isOpen={!!chooser.file?.open}
          onClose={() => setChooser("file", false)}
          onComplete={(_file, name, fileSrc) => {
            commands.insertFile?.({ name, src: fileSrc });
            setChooser("file", false);
          }}
        />
      );
    },
  },
  {
    name: "table",
    icon: TableIcon,
    onClick: ({ commands, isActive }) => {
      if (isActive) return;
      commands.insertTable?.();
    },
  },
  {
    name: "iframe",
    icon: FileCodeIcon,
    onClick: ({ commands, isActive, selectedText }) => {
      if (isActive) {
        commands.removeLink?.();
      } else {
        setChooser("iframe", !chooser.iframe?.open, selectedText);
      }
    },
    MenuItem: ({ RenderItem, itemProps }) => {
      const { commands } = useEditorContext();
      return (
        <IFrameChooser
          trigger={
            <div>
              <RenderItem {...itemProps} />
            </div>
          }
          isOpen={!!chooser.iframe?.open}
          onClose={() => setChooser("iframe", false)}
          onComplete={(iFrameURL) => {
            const options: { src: string; title?: string; allow?: string } = {
              src: "",
            };
            const match = iFrameURL.match(iframeRegex);
            if (match) {
              options.src = extractHTMLTag(match[0], "src") || "";
              options.title = extractHTMLTag(match[0], "title") ?? "iFrame";
              options.allow = extractHTMLTag(match[0], "allow") ?? "";
            } else {
              options.src = iFrameURL;
            }

            if (options.src) {
              commands.addIframe?.(options);
            }
            setChooser("iframe", false);
          }}
        />
      );
    },
  },
];
